import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { PARTS_FLOW_ENABLED } from '../config/features';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatAccountLabel, getPhoneDisplayParts } from '../utils/chatIdentity';
import { formatChatThreadTitle, formatSidebarTitle } from '../utils/titleNaming';
import { driverSiteUrl, isPartnerSite } from '../utils/siteHost';
import {
    CONVERSATION_KIND_DEFINITIONS,
    formatTimeAgo,
    getPinnedConversationStorageKey,
    inferConversationKind,
    readLocalStringSet,
    writeLocalStringSet,
} from '../utils/coreAppEnhancements';
import { getPartnerStatusLabel } from '../utils/partnerShopApi';
import VehicleSelector from './VehicleSelector';
import DriverAvatar, { SHOP_GLYPH } from './DriverAvatar';


function isMonitoringDiagnosticTicket(ticket) {
    const stage = String(ticket?.stage || ticket?.ticketStage || '').toLowerCase();
    const status = String(ticket?.status || ticket?.ticketStatus || '').toLowerCase();
    if (status === 'closed' || status === 'resolved' || status === 'abandoned') return false;
    return stage === 'monitoring' || status === 'awaiting_outcome' || status === 'awaiting_confirmation';
}

export function getDiagnosticStatusLabel(ticket) {
    const status = String(ticket?.status || ticket?.ticketStatus || '').toLowerCase();
    if (status === 'ready_for_review') return 'Report Ready';
    // A closed/resolved ticket reads as "Resolved" to the user (abandoned stays distinct).
    if (status === 'closed' || status === 'resolved') return 'Resolved';
    if (status === 'abandoned') return 'Abandoned';
    if (isMonitoringDiagnosticTicket(ticket)) return 'Monitoring';
    if (status === 'in_progress') return 'In Progress';
    return 'Open';
}

function getDiagnosticStatusClasses(ticket) {
    const status = String(ticket?.status || ticket?.ticketStatus || '').toLowerCase();
    if (status === 'ready_for_review') return 'bg-[#e8fbef] text-[#15803d]';
    // Resolved → green; abandoned keeps the muted grey.
    if (status === 'closed' || status === 'resolved') return 'bg-[#e8fbef] text-[#15803d]';
    if (status === 'abandoned') return 'bg-black/8 text-black/45';
    // Monitoring → amber, distinct from active "Open" red.
    if (isMonitoringDiagnosticTicket(ticket)) return 'bg-[#fef3c7] text-[#b45309]';
    return 'bg-[#FDEDEE] text-[#D81F26]';
}

function getDiagnosticStatusRank(ticket) {
    const status = String(ticket?.status || ticket?.ticketStatus || '').toLowerCase();
    if (status === 'closed' || status === 'resolved' || status === 'abandoned') return 3;
    if (isMonitoringDiagnosticTicket(ticket)) return 2;
    if (status === 'ready_for_review') return 2;
    if (status === 'open' || status === 'in_progress') return 1;
    return 0;
}

export function isOpenDiagnosticTicket(ticket) {
    const status = String(ticket?.status || ticket?.ticketStatus || '').toLowerCase();
    return status !== 'closed' && status !== 'resolved' && status !== 'abandoned';
}

export function getDiagnosticTimestamp(ticket) {
    const raw = (
        ticket?.last_activity_at ||
        ticket?.lastActivityAt ||
        ticket?.last_activity ||
        ticket?.lastActivity ||
        ticket?.updated_at ||
        ticket?.updatedAt ||
        ticket?.created_at ||
        ticket?.createdAt ||
        ticket?.opened_at ||
        ticket?.openedAt
    );
    const time = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(time) ? time : 0;
}

function getDiagnosticActivityDate(ticket) {
    const time = getDiagnosticTimestamp(ticket);
    return time ? new Date(time) : null;
}

function getThreadTimestamp(thread) {
    // Pin the Add-Vehicle tab to the very top of the conversation list.
    if (thread?.__addVehicleTab) return Number.MAX_SAFE_INTEGER;
    const raw = (
        thread?.last_activity ||
        thread?.lastActivity ||
        thread?.diagnostic_ticket_last_activity_at ||
        thread?.diagnosticTicketLastActivityAt ||
        thread?.diagnostic_ticket?.last_activity_at ||
        thread?.diagnosticTicket?.lastActivityAt ||
        thread?.updated_at ||
        thread?.updatedAt ||
        thread?.opened_at ||
        thread?.openedAt
    );
    const time = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(time) ? time : 0;
}

function getThreadActivityDate(thread) {
    const raw = (
        thread?.last_activity ||
        thread?.lastActivity ||
        thread?.diagnostic_ticket_last_activity_at ||
        thread?.diagnosticTicketLastActivityAt ||
        thread?.diagnostic_ticket?.last_activity_at ||
        thread?.diagnosticTicket?.lastActivityAt ||
        thread?.updated_at ||
        thread?.updatedAt ||
        thread?.opened_at ||
        thread?.openedAt
    );
    const date = raw ? new Date(raw) : null;
    return date && Number.isFinite(date.getTime()) ? date : null;
}

function formatSidebarActivityTime(date) {
    if (!date) return '';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDiff = Math.round((today - targetDay) / 86400000);
    const timeLabel = new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);

    if (dayDiff <= 0) return timeLabel;
    if (dayDiff === 1) return `${timeLabel} Yday`;

    const dateOptions = dayDiff < 7
        ? { weekday: 'short' }
        : date.getFullYear() === now.getFullYear()
            ? { month: 'short', day: 'numeric' }
            : { month: 'short', day: 'numeric', year: 'numeric' };

    return `${timeLabel} ${new Intl.DateTimeFormat(undefined, dateOptions).format(date)}`;
}

function formatSidebarActivityTooltip(date, label) {
    if (!date) return '';
    return `${label} ${new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(date)}`;
}

function formatThreadLastChatTime(thread) {
    return formatSidebarActivityTime(getThreadActivityDate(thread));
}

function formatThreadLastChatTooltip(thread) {
    return formatSidebarActivityTooltip(getThreadActivityDate(thread), 'Last chat');
}

function formatDiagnosticActivityTime(ticket) {
    return formatSidebarActivityTime(getDiagnosticActivityDate(ticket));
}

function formatDiagnosticActivityTooltip(ticket) {
    return formatSidebarActivityTooltip(getDiagnosticActivityDate(ticket), 'Diagnostic updated');
}

export function sortDiagnosticCards(cards = []) {
    return [...cards].sort((a, b) => {
        const aOpen = isOpenDiagnosticTicket(a) ? 1 : 0;
        const bOpen = isOpenDiagnosticTicket(b) ? 1 : 0;
        if (aOpen !== bOpen) return bOpen - aOpen;

        const timeDiff = getDiagnosticTimestamp(b) - getDiagnosticTimestamp(a);
        if (timeDiff !== 0) return timeDiff;

        return getDiagnosticStatusRank(a) - getDiagnosticStatusRank(b);
    });
}

function sortThreads(threads = []) {
    return [...threads].sort((a, b) => getThreadTimestamp(b) - getThreadTimestamp(a));
}

function getTicketVehicleLabel(ticket) {
    if (ticket?.vehicle_label) return ticket.vehicle_label;
    if (ticket?.vehicleLabel) return ticket.vehicleLabel;
    const vehicle = ticket?.vehicle || {};
    return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
}

function getDiagnosticTicketTitle(ticket) {
    if (!ticket) return '';
    const rawTitle = ticket?.title || ticket?.symptom_summary || '';
    return formatSidebarTitle(rawTitle, 'Vehicle Diagnosis');
}

function getDiagnosticTargetThreadId(ticket) {
    return ticket?.thread_id || ticket?.threadId || ticket?.source_repair_log_id || ticket?.sourceRepairLogId || ticket?.repair_log_id || ticket?.repairLogId || '';
}

function getThreadTitle(thread) {
    if (thread?.__addVehicleTab) return 'Add Vehicle';
    return formatChatThreadTitle(thread?.title || thread?.name || '', 'New Chat');
}

function getThreadVehicleLabel(thread) {
    if (thread?.vehicle_label) return thread.vehicle_label;
    if (thread?.vehicleLabel) return thread.vehicleLabel;
    const vehicle = thread?.vehicle || {};
    return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
}

export function getThreadDiagnosticTicket(thread) {
    if (!thread) return null;
    const ticket = thread.diagnostic_ticket || thread.diagnosticTicket || null;
    const ticketId = thread.diagnostic_ticket_id || thread.diagnosticTicketId || ticket?.diagnostic_ticket_id || ticket?.diagnosticTicketId;
    const explicitType = thread.conversation_type || thread.conversationType || '';
    const isDiagnosis = explicitType ? explicitType === 'diagnosis' : Boolean(ticketId);
    if (!isDiagnosis) return null;

    return {
        ...(ticket || {}),
        diagnostic_ticket_id: ticketId || ticket?.diagnostic_ticket_id || ticket?.diagnosticTicketId,
        ticket_number: thread.diagnostic_ticket_number || thread.diagnosticTicketNumber || ticket?.ticket_number || ticket?.ticketNumber,
        title: thread.diagnostic_ticket_title || thread.diagnosticTicketTitle || ticket?.title,
        symptom_summary: thread.diagnostic_ticket_symptom_summary || thread.diagnosticTicketSymptomSummary || ticket?.symptom_summary,
        status: thread.diagnostic_ticket_status || thread.diagnosticTicketStatus || ticket?.status,
        stage: thread.diagnostic_ticket_stage || thread.diagnosticTicketStage || ticket?.stage,
        source_repair_log_id: thread.diagnostic_ticket_source_repair_log_id || thread.diagnosticTicketSourceRepairLogId || ticket?.source_repair_log_id || ticket?.sourceRepairLogId || thread.thread_id,
        thread_id: ticket?.thread_id || ticket?.threadId || thread.thread_id,
        vehicle_id: thread.vehicle_id || ticket?.vehicle_id,
        vehicle_label: getThreadVehicleLabel(thread) || getTicketVehicleLabel(ticket),
        last_activity_at: ticket?.last_activity_at || ticket?.lastActivityAt || thread.last_activity,
    };
}

function getDiagnosticIdentityKey(ticket) {
    const ticketId = ticket?.diagnostic_ticket_id || ticket?.diagnosticTicketId || ticket?.ticket_id || ticket?.ticketId || ticket?.id;
    if (ticketId) return `ticket:${ticketId}`;

    const threadId = ticket?.thread_id || ticket?.threadId || ticket?.source_repair_log_id || ticket?.sourceRepairLogId || ticket?.repair_log_id || ticket?.repairLogId;
    if (threadId) return `thread:${threadId}`;

    return '';
}

function normalizeDiagnosticIssueKey(ticket) {
    const rawTitle = getDiagnosticTicketTitle(ticket).toLowerCase();
    const normalized = rawTitle
        .replace(/won['’]?t/g, 'will not')
        .replace(/\bcan['’]?t\b/g, 'cannot')
        .replace(/\bdoesn['’]?t\b/g, 'does not')
        .replace(/\bisn['’]?t\b/g, 'is not')
        .replace(/\bno[\s-]*start(?:ing)?\b/g, 'no start')
        .replace(/\bnot starting\b/g, 'no start')
        .replace(/\bwill not start\b/g, 'no start')
        .replace(/\bwon?t start\b/g, 'no start')
        .replace(/\bcar\b/g, ' ')
        .replace(/\bvehicle\b/g, ' ')
        .replace(/\bmy\b/g, ' ')
        .replace(/\bis\b/g, ' ')
        .replace(/\bthe\b/g, ' ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\bstarting\b/g, 'start')
        .replace(/\bstarts\b/g, 'start')
        .replace(/\s+/g, ' ')
        .trim();

    if (
        normalized.includes('start') &&
        (normalized.includes('no start') || normalized.includes('not start') || normalized.includes('cannot start'))
    ) {
        return 'no-start';
    }

    return normalized;
}

function dedupeDiagnosticCards(cards) {
    const seen = new Map();
    const uniqueCards = [];

    for (const card of cards || []) {
        const titleKey = normalizeDiagnosticIssueKey(card);
        const vehicleKey = getTicketVehicleLabel(card).trim().toLowerCase();
        const identityKey = getDiagnosticIdentityKey(card);
        const dedupeKey = identityKey || `${titleKey}::${vehicleKey}`;
        if (seen.has(dedupeKey)) {
            const existingIndex = seen.get(dedupeKey);
            const existingCard = uniqueCards[existingIndex];
            const cardOpen = isOpenDiagnosticTicket(card);
            const existingOpen = isOpenDiagnosticTicket(existingCard);
            const shouldReplaceExisting = (
                (!cardOpen && existingOpen) ||
                (cardOpen === existingOpen && getDiagnosticTimestamp(card) > getDiagnosticTimestamp(existingCard)) ||
                (cardOpen === existingOpen && getDiagnosticStatusRank(card) > getDiagnosticStatusRank(existingCard))
            );
            if (shouldReplaceExisting) {
                uniqueCards[existingIndex] = {
                    ...existingCard,
                    ...card,
                    display_ticket_number: existingCard.display_ticket_number || card.display_ticket_number,
                    ticket_number: existingCard.ticket_number || card.ticket_number,
                };
            }
            continue;
        }
        seen.set(dedupeKey, uniqueCards.length);
        uniqueCards.push(card);
    }

    return uniqueCards;
}

const CONVERSATION_KIND_BY_ID = CONVERSATION_KIND_DEFINITIONS.reduce((lookup, kind) => {
    lookup[kind.id] = kind;
    return lookup;
}, {});

const ChatBubbleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
);

const WrenchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.7-3.7a6 6 0 0 1-7.9 7.9l-6.9 6.9a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.9-7.9z" />
    </svg>
);

const PartsIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 7h10v10H7z" />
        <path d="M4 10h3M17 10h3M4 14h3M17 14h3M10 4v3M14 4v3M10 17v3M14 17v3" />
    </svg>
);

const MaintenanceIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 2v4M16 2v4M3 10h18" />
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="m9 16 2 2 4-5" />
    </svg>
);

const ServiceIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 2v20l3-2 3 2 3-2 3 2 4-2V2z" />
        <path d="M8 7h8M8 11h8M8 15h5" />
    </svg>
);

const ModIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z" />
        <path d="M5 19h14" />
    </svg>
);

const ShopIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9 5 3h14l2 6" />
        <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
        <path d="M9 22V12h6v10" />
    </svg>
);

const getConversationIcon = (kind) => {
    if (kind === 'diagnosis') return <WrenchIcon />;
    if (kind === 'parts') return <PartsIcon />;
    if (kind === 'maintenance') return <MaintenanceIcon />;
    if (kind === 'service') return <ServiceIcon />;
    if (kind === 'modification') return <ModIcon />;
    if (kind === 'shop') return <ShopIcon />;
    return <ChatBubbleIcon />;
};

const getConversationIconClass = (kind) => {
    if (kind === 'diagnosis') return 'text-[#414141]';
    if (kind === 'parts') return 'text-[#7c3aed]';
    if (kind === 'maintenance') return 'text-[#0f8a6a]';
    if (kind === 'service') return 'text-[#b7791f]';
    if (kind === 'modification') return 'text-[#c2410c]';
    return 'text-[#414141]';
};

const ChatSidebar = ({
    authState,
    adminAuthState,
    displayName = '',
    avatarUrl = '',
    // Shop portal only: the shop's photo. Kept apart from avatarUrl so a driver's own
    // picture is never shown in a shop admin view.
    shopPhotoUrl = '',
    onOpenAuthModal,
    onLogout,
    isOpen,
    isCollapsed,
    onToggleCollapse,
    onClose,
    onOpenHelp,
    onOpenOnboarding,
    threads = [],
    currentThreadId = null,
    addVehicleThreadIds = [],
    onNewThread,
    onSelectThread,
    onRenameThread,
    onDeleteThread,
    onArchiveThread,
    onRestoreThread,
    fetchArchivedThreads,
    notificationsUnread = 0,
    recentDiagnostics = [],
    activeDiagnosticTicket = null,
    threadsLoading = false,
    diagnosticsLoading = false,
    isAdminView: isAdminViewProp,
    // Partner-estimate (shop) conversations for the driver sidebar — each becomes a row
    // titled with the shop's name that opens the shop chat pane in ChatPage.
    garageRequests = [],
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const addVehicleSet = useMemo(() => new Set((addVehicleThreadIds || []).map(String)), [addVehicleThreadIds]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
    const [conversationFilter, setConversationFilter] = useState('all');
    const [conversationSearch, setConversationSearch] = useState('');
    const pinnedStorageKey = useMemo(() => getPinnedConversationStorageKey(authState?.identity), [authState?.identity]);
    const [pinnedConversationKeys, setPinnedConversationKeys] = useState(() => readLocalStringSet(pinnedStorageKey));
    // Per-row action menu (Rename / Archive / Delete) + inline title editing.
    const [openActionMenuKey, setOpenActionMenuKey] = useState(null);
    const [editingKey, setEditingKey] = useState(null);
    const [editingThreadId, setEditingThreadId] = useState(null);
    const [editingTicketId, setEditingTicketId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    // Archived conversations view.
    const [showArchived, setShowArchived] = useState(false);
    const [archivedItems, setArchivedItems] = useState([]);
    const [archivedLoading, setArchivedLoading] = useState(false);
    const actionMenuRef = useRef(null);

    // The admin cookie is a permission for /shop, not a global UI mode: only the
    // route decides whether this sidebar is the shop menu or the chat menu.
    const isAdminAuthenticated = adminAuthState?.status === 'authenticated';
    // The partner portal serves its dashboard at "/", so it passes this in. On the driver site
    // the admin lives at /shop and its sub-routes (e.g. /shop/calendar), which must all show the
    // shop-admin nav — not the consumer nav.
    const isAdminView = isAdminViewProp ?? (location.pathname === '/shop' || location.pathname.startsWith('/shop/'));
    const isAuthenticated = authState?.status === 'authenticated';
    // Shop admin view: the shop photo (PartnerShell passes it); driver site: the profile picture.
    const sidebarAvatarUrl = isAdminView ? shopPhotoUrl : (isAuthenticated ? avatarUrl : '');
    const sidebarAvatarGlyph = isAdminView ? SHOP_GLYPH : undefined;
    const isLoggedIn = isAdminView ? isAdminAuthenticated : isAuthenticated;
    const isLoading = isAdminView ? adminAuthState?.status === 'loading' : authState?.status === 'loading';
    const testerMode = localStorage.getItem('TesterMode') === 'yes';
    const effectiveCollapsed = isCollapsed && isDesktop;
    const accountPhoneParts = useMemo(() => getPhoneDisplayParts(authState?.phone), [authState?.phone]);

    useEffect(() => {
        setPinnedConversationKeys(readLocalStringSet(pinnedStorageKey));
    }, [pinnedStorageKey]);

    const recentChatThreads = useMemo(() => (
        sortThreads(threads.filter((thread) => thread?.thread_id)).slice(0, 20)
    ), [threads]);

    const liveActiveDiagnosticCard = useMemo(() => (activeDiagnosticTicket?.title
        ? {
            ...activeDiagnosticTicket,
            thread_id: activeDiagnosticTicket.thread_id || currentThreadId,
            source_repair_log_id: activeDiagnosticTicket.source_repair_log_id || activeDiagnosticTicket.thread_id || currentThreadId,
            status: activeDiagnosticTicket.status || 'open',
        }
        : null
    ), [activeDiagnosticTicket, currentThreadId]);

    const currentThreadAlreadyListed = useCallback((candidateCard) => candidateCard
        ? recentDiagnostics.some((ticket) => {
            const targetThreadId = getDiagnosticTargetThreadId(ticket);
            const candidateThreadId = getDiagnosticTargetThreadId(candidateCard);
            if (targetThreadId && candidateThreadId && String(targetThreadId) === String(candidateThreadId)) return true;

            const ticketTitleKey = normalizeDiagnosticIssueKey(ticket);
            const currentTitleKey = normalizeDiagnosticIssueKey(candidateCard);
            const ticketVehicleKey = getTicketVehicleLabel(ticket).trim().toLowerCase();
            const currentVehicleKey = getTicketVehicleLabel(candidateCard).trim().toLowerCase();

            return ticketTitleKey === currentTitleKey && ticketVehicleKey === currentVehicleKey;
        })
        : false, [recentDiagnostics]);

    const diagnosticCards = useMemo(() => sortDiagnosticCards(dedupeDiagnosticCards([
        ...(liveActiveDiagnosticCard && !currentThreadAlreadyListed(liveActiveDiagnosticCard) ? [liveActiveDiagnosticCard] : []),
        ...recentDiagnostics,
    ])), [liveActiveDiagnosticCard, recentDiagnostics, currentThreadAlreadyListed]);

    const diagnosticTicketByThreadId = useMemo(() => diagnosticCards.reduce((lookup, ticket) => {
        const targetThreadId = getDiagnosticTargetThreadId(ticket);
        if (!targetThreadId) return lookup;
        const key = String(targetThreadId);
        const existing = lookup.get(key);
        if (!existing || getDiagnosticStatusRank(ticket) > getDiagnosticStatusRank(existing)) {
            lookup.set(key, ticket);
        }
        return lookup;
    }, new Map()), [diagnosticCards]);

    const conversationItems = useMemo(() => {
        const listedThreadIds = new Set(recentChatThreads.map((thread) => String(thread.thread_id)));
        const threadItems = recentChatThreads.map((thread) => {
            const threadId = thread.thread_id;
            const vehicleLabel = getThreadVehicleLabel(thread);
            // Status source: the thread carries a cached diagnostic_ticket_status from
            // list_threads, but that lags behind a just-closed ticket (it only updates on
            // a full thread refetch/reload). The refreshed diagnostics list
            // (diagnosticTicketByThreadId) reflects the close immediately. A close is
            // terminal, so if EITHER source reports Closed, show Closed — otherwise the
            // pill sticks on "Open" after the user closes a ticket.
            const threadTicket = getThreadDiagnosticTicket(thread);
            const explicitConversationType = thread.conversation_type || thread.conversationType || '';
            const refreshedTicket = (!explicitConversationType || explicitConversationType === 'diagnosis')
                ? diagnosticTicketByThreadId.get(String(threadId))
                : null;
            let ticket = threadTicket || refreshedTicket;
            // The cached thread status lags the live diagnostics list. Adopt the refreshed
            // status/stage when the ticket has since CLOSED (terminal) or moved to MONITORING
            // — otherwise the pill sticks on "Open" after a repair completes.
            if (
                threadTicket && refreshedTicket &&
                (
                    (isOpenDiagnosticTicket(threadTicket) && !isOpenDiagnosticTicket(refreshedTicket)) ||
                    isMonitoringDiagnosticTicket(refreshedTicket)
                )
            ) {
                ticket = { ...threadTicket, status: refreshedTicket.status || threadTicket.status || 'closed', stage: refreshedTicket.stage || threadTicket.stage };
            }
            const timeLabel = formatThreadLastChatTime(thread);
            const rawTitle = getThreadTitle(thread);
            const title = addVehicleSet.has(String(threadId)) && rawTitle === 'Add Vehicle' ? 'Add Vehicle' : rawTitle;
            const statusLabel = ticket ? getDiagnosticStatusLabel(ticket) : '';
            const rawSearchText = [title, vehicleLabel, statusLabel, thread?.summary, thread?.last_message, thread?.lastMessage].filter(Boolean).join(' ');
            const explicitKind = thread.conversation_type || thread.conversationType || '';
            const kind = CONVERSATION_KIND_BY_ID[explicitKind]
                ? explicitKind
                : inferConversationKind({ title, searchText: rawSearchText, ticket });
            const kindLabel = CONVERSATION_KIND_BY_ID[kind]?.label || 'General';
            return {
                key: `thread:${threadId}`,
                kind,
                kindLabel,
                threadId,
                ticketId: ticket?.diagnostic_ticket_id || null,
                title,
                vehicleLabel,
                timeLabel,
                timeTooltip: formatThreadLastChatTooltip(thread),
                timestamp: getThreadTimestamp(thread),
                statusLabel,
                statusClasses: ticket ? getDiagnosticStatusClasses(ticket) : '',
                disabled: false,
                isActive: String(threadId) === String(currentThreadId || ''),
                searchText: [rawSearchText, kindLabel, kind].filter(Boolean).join(' ').toLowerCase(),
            };
        });

        const diagnosticItems = diagnosticCards
            .filter((ticket) => {
                const targetThreadId = getDiagnosticTargetThreadId(ticket);
                return !targetThreadId || !listedThreadIds.has(String(targetThreadId));
            })
            .map((ticket, index) => {
                const targetThreadId = getDiagnosticTargetThreadId(ticket);
                const vehicleLabel = getTicketVehicleLabel(ticket);
                const title = getDiagnosticTicketTitle(ticket);
                const statusLabel = getDiagnosticStatusLabel(ticket);
                const kind = inferConversationKind({ title, searchText: [vehicleLabel, statusLabel].join(' '), ticket });
                const kindLabel = CONVERSATION_KIND_BY_ID[kind]?.label || 'Diagnosis';
                return {
                    key: `diagnosis:${getDiagnosticIdentityKey(ticket) || `${title}-${index}`}`,
                    kind,
                    kindLabel,
                    threadId: targetThreadId,
                    ticketId: ticket?.diagnostic_ticket_id || null,
                    title,
                    vehicleLabel,
                    timeLabel: formatDiagnosticActivityTime(ticket),
                    timeTooltip: formatDiagnosticActivityTooltip(ticket),
                    timestamp: getDiagnosticTimestamp(ticket),
                    statusLabel,
                    statusClasses: getDiagnosticStatusClasses(ticket),
                    disabled: !targetThreadId,
                    isActive: targetThreadId && String(targetThreadId) === String(currentThreadId || ''),
                    searchText: [title, vehicleLabel, statusLabel, kindLabel, kind].filter(Boolean).join(' ').toLowerCase(),
                };
            });

        // Partner-estimate (shop) conversations. Each sent estimate becomes its own row,
        // titled with the SHOP's name, that opens the shop chat pane. Driver view only — the
        // shop admin portal has its own job list.
        const garageItems = (isAdminView ? [] : garageRequests)
            .filter((request) => request?.request_id)
            .map((request) => {
                const shopName = request?.partner_shop?.name || request?.partner_shop?.shop_name || 'Shop';
                const v = request?.vehicle || {};
                const vehicleLabel = [v.year, v.make, v.model].filter(Boolean).join(' ');
                const statusLabel = getPartnerStatusLabel(request?.request_status, 'driver');
                const when = request?.updated_at || request?.created_at || '';
                const serviceTitle = Array.isArray(request?.services) && request.services.length
                    ? request.services.map((s) => s?.title).filter(Boolean).join(', ')
                    : '';
                return {
                    key: `garage:${request.request_id}`,
                    kind: 'shop',
                    kindLabel: 'Shop',
                    threadId: null,
                    ticketId: null,
                    garageRequestId: request.request_id,
                    garageRequest: request,
                    title: shopName,
                    vehicleLabel,
                    timeLabel: when ? formatTimeAgo(when) : '',
                    timeTooltip: when ? new Date(when).toLocaleString() : '',
                    timestamp: when ? (Date.parse(when) || 0) : 0,
                    statusLabel,
                    statusClasses: 'bg-[#ecfff5] text-[#087a43]',
                    disabled: false,
                    isActive: false,
                    searchText: [shopName, vehicleLabel, statusLabel, serviceTitle, 'shop']
                        .filter(Boolean).join(' ').toLowerCase(),
                };
            });

        return [...threadItems, ...diagnosticItems, ...garageItems]
            .map((item) => ({
                ...item,
                isPinned: pinnedConversationKeys.has(item.key),
            }))
            .sort((a, b) => {
                if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
                return b.timestamp - a.timestamp;
            });
    }, [recentChatThreads, diagnosticCards, diagnosticTicketByThreadId, currentThreadId, pinnedConversationKeys, addVehicleSet, garageRequests, isAdminView]);

    const conversationFilters = useMemo(() => {
        const presentKinds = new Set(conversationItems.map((item) => item.kind));
        return CONVERSATION_KIND_DEFINITIONS.filter((filter) => (
            (PARTS_FLOW_ENABLED || filter.id !== 'parts') &&
            (filter.id === 'all' || presentKinds.has(filter.id))
        ));
    }, [conversationItems]);

    const visibleConversationItems = useMemo(() => {
        const normalizedSearch = conversationSearch.trim().toLowerCase();
        return conversationItems.filter((item) => {
            const matchesFilter = conversationFilter === 'all' || item.kind === conversationFilter;
            const matchesSearch = !normalizedSearch || item.searchText.includes(normalizedSearch);
            return matchesFilter && matchesSearch;
        });
    }, [conversationItems, conversationFilter, conversationSearch]);

    const conversationsLoading = threadsLoading || diagnosticsLoading;

    useEffect(() => {
        if (conversationFilter === 'all') return;
        if (!conversationFilters.some((filter) => filter.id === conversationFilter)) {
            setConversationFilter('all');
        }
    }, [conversationFilter, conversationFilters]);

    const togglePinnedConversation = useCallback((itemKey) => {
        setPinnedConversationKeys((prev) => {
            const next = new Set(prev);
            if (next.has(itemKey)) next.delete(itemKey);
            else next.add(itemKey);
            writeLocalStringSet(pinnedStorageKey, next);
            return next;
        });
    }, [pinnedStorageKey]);

    const loadArchived = useCallback(async () => {
        if (!fetchArchivedThreads) return;
        setArchivedLoading(true);
        try {
            const rows = await fetchArchivedThreads();
            setArchivedItems(Array.isArray(rows) ? rows : []);
        } finally {
            setArchivedLoading(false);
        }
    }, [fetchArchivedThreads]);

    // Load (and refresh) the archived list whenever the Archived view is opened.
    useEffect(() => {
        if (showArchived) loadArchived();
    }, [showArchived, loadArchived]);

    const startRename = useCallback((item) => {
        setOpenActionMenuKey(null);
        setEditingKey(item.key);
        setEditingThreadId(item.threadId || null);
        setEditingTicketId(item.ticketId || null);
        setEditingTitle(item.title || '');
    }, []);

    const cancelRename = useCallback(() => {
        setEditingKey(null);
        setEditingThreadId(null);
        setEditingTicketId(null);
        setEditingTitle('');
    }, []);

    const commitRename = useCallback(() => {
        const threadId = editingThreadId;
        const ticketId = editingTicketId;
        const nextTitle = editingTitle.trim();
        setEditingKey(null);
        setEditingThreadId(null);
        setEditingTicketId(null);
        setEditingTitle('');
        if (!nextTitle || (threadId == null && ticketId == null)) return;
        // The server locks the title on a manual rename so the auto-titler never
        // overwrites it again; a diagnosis conversation also renames its ticket.
        if (onRenameThread) onRenameThread(threadId, nextTitle, ticketId);
    }, [editingThreadId, editingTicketId, editingTitle, onRenameThread]);

    const handleArchive = useCallback((threadId) => {
        setOpenActionMenuKey(null);
        if (onArchiveThread) onArchiveThread(threadId);
    }, [onArchiveThread]);

    const handleRestore = useCallback(async (threadId) => {
        if (onRestoreThread) {
            const ok = await onRestoreThread(threadId);
            if (ok !== false) setArchivedItems((prev) => prev.filter((t) => String(t.thread_id) !== String(threadId)));
        }
    }, [onRestoreThread]);

    // Close the per-row action menu when clicking elsewhere.
    useEffect(() => {
        if (!openActionMenuKey) return undefined;
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setOpenActionMenuKey(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openActionMenuKey]);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNavClick = (path) => {
        if (location.pathname !== path) navigate(path);
        if (window.innerWidth < 768) onClose();
    };

    // On the partner portal the logo leaves for the driver site (there is no
    // useful in-app "home" — "/" is already the Lead Inbox), so it renders as a
    // real anchor opening a new tab: the shop keeps its dashboard, and hover
    // preview / middle-click / "open in new tab" all behave natively.
    const logoIsExternal = isPartnerSite();
    const logoProps = logoIsExternal
        ? {
            as: 'a',
            href: driverSiteUrl(),
            target: '_blank',
            rel: 'noopener noreferrer',
            title: 'Open CarFido in a new tab',
        }
        : { as: 'div', onClick: () => handleNavClick('/') };
    const { as: LogoTag, ...logoAnchorProps } = logoProps;

    const handleNewThread = useCallback(async () => {
        if (onNewThread) {
            await onNewThread();
            if (location.pathname !== '/chat' && location.pathname !== '/test-chat') navigate('/chat');
            if (window.innerWidth < 768) onClose();
        }
    }, [onNewThread, location.pathname, navigate, onClose]);

    const handleSelectThread = useCallback((threadId) => {
        if (threadId !== currentThreadId && onSelectThread) onSelectThread(threadId);
        if (location.pathname !== '/chat' && location.pathname !== '/test-chat') navigate('/chat');
        if (window.innerWidth < 768) onClose();
    }, [onSelectThread, currentThreadId, location.pathname, navigate, onClose]);

    // Open a shop (partner-estimate) conversation: ChatPage consumes openGarageRequestId from
    // navigation state and switches to the shop chat pane for that request.
    const handleOpenGarageRequest = useCallback((requestId, requestObj) => {
        if (!requestId) return;
        // Pass the full request so ChatPage can open it directly, even when it isn't part of
        // the currently-active diagnostic ticket's request list.
        navigate('/chat', { state: { openGarageRequestId: requestId, openGarageRequest: requestObj || null } });
        if (window.innerWidth < 768) onClose();
    }, [navigate, onClose]);

    const shopAdminNavItem = {
        name: isPartnerSite() ? 'Lead Inbox' : 'Shop Admin',
        path: isPartnerSite() ? '/' : '/shop',
        icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9 5 3h14l2 6" /><path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /><path d="M9 22V12h6v10" /></svg>,
    };
    // Calendar (scheduled jobs) — partner portal serves it at /calendar; the driver-site admin
    // view (dev) reaches the dashboard at /shop, so the calendar lives at /shop/calendar there.
    const shopCalendarNavItem = {
        name: 'Calendar',
        path: isPartnerSite() ? '/calendar' : '/shop/calendar',
        icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>,
    };
    const shopNotificationsNavItem = {
        name: 'Notifications',
        path: isPartnerSite() ? '/notifications' : '/shop/notifications',
        badge: notificationsUnread,
        icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>,
    };
    // Settings lives on the partner portal only. The driver site's admin view reaches
    // this sidebar at /shop, where no /settings route exists.
    const shopSettingsNavItem = {
        name: 'Settings',
        path: '/settings',
        icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>,
    };
    const navItems = isAdminView ? [shopAdminNavItem, shopCalendarNavItem, ...(isPartnerSite() ? [shopNotificationsNavItem, shopSettingsNavItem] : [])] : [
        ...(!isAuthenticated ? [{
            name: 'Chat',
            path: '/chat',
            activePaths: ['/chat', '/test-chat'],
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></svg>,
        }] : []),
        { name: 'Garage', path: '/garage', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" /><circle cx="7" cy="17" r="2" /><path d="M9 17h6" /><circle cx="17" cy="17" r="2" /></svg> },
        { name: 'Glove Box', path: '/glovebox', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg> },
        { name: 'Notifications', path: '/notifications', badge: notificationsUnread, icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg> },
        ...(isAdminAuthenticated ? [shopAdminNavItem] : []),
    ];

    return (
        <div className={`
            fixed md:sticky top-0 left-0 z-[60] h-[100dvh] bg-[#F9F9F9] flex flex-col border-r border-[#E6E6E6] shrink-0 transition-[width,transform] duration-200 ease-out py-2
            ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            ${effectiveCollapsed ? 'md:w-[64px]' : 'md:w-80 w-80'}
        `}>
            <div className={`flex-1 min-h-0 p-2 relative flex flex-col ${effectiveCollapsed ? 'items-center' : ''}`}>
                <button
                    onClick={onClose}
                    className="md:hidden absolute top-4 right-4 p-2 text-black/40 hover:text-black transition-colors"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>

                <div className={`flex items-center justify-between mb-3 px-2 w-full shrink-0 ${effectiveCollapsed ? 'flex-col gap-4' : 'flex-row'}`}>
                    {!effectiveCollapsed ? (
                        <LogoTag
                            {...logoAnchorProps}
                            className="flex items-center gap-1 cursor-pointer no-underline hover:opacity-80 transition-opacity"
                        >
                            {/* No drop-shadow: the mark is downscaled from 600px, and a blur on
                                top of that softens its edges into looking washed out. Clean
                                artwork on a light sidebar reads sharper with no shadow at all. */}
                            <img
                                src="/PNG/carfido-icon-transparent.png"
                                alt="CarFido"
                                className="w-11 h-11 object-contain transition-transform duration-200 hover:scale-[1.06]"
                            />
                            <span className="font-extrabold text-[21px] leading-none text-black tracking-tight whitespace-nowrap">CarFido</span>
                            {/* Tells the shop this leaves the portal before they click. */}
                            {logoIsExternal && (
                                <svg
                                    className="ml-0.5 h-3.5 w-3.5 shrink-0 text-black/30"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M14 5h5v5" />
                                    <path d="M19 5l-8 8" />
                                    <path d="M19 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4" />
                                </svg>
                            )}
                        </LogoTag>
                    ) : (
                        <LogoTag
                            {...logoAnchorProps}
                            className="block cursor-pointer no-underline"
                        >
                            <img
                                src="/PNG/carfido-icon-transparent.png"
                                alt="CarFido"
                                className="w-11 h-11 object-contain hover:scale-[1.06] transition-transform duration-200"
                            />
                        </LogoTag>
                    )}
                    <button
                        onClick={onToggleCollapse}
                        className="p-2 rounded-xl hover:bg-black/5 text-black/15 hover:text-black transition-[background-color,color,transform] duration-150 ease-out hidden md:flex active:scale-95"
                        title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="3" x2="9" y2="21"></line>
                        </svg>
                    </button>
                </div>

                {isAuthenticated && !isAdminView && (
                    <div className={`w-full shrink-0 mb-3 ${effectiveCollapsed ? 'flex justify-center' : 'px-2'}`}>
                        <VehicleSelector
                            authState={authState}
                            variant="sidebar"
                            collapsed={effectiveCollapsed}
                            onNavigate={handleNavClick}
                        />
                    </div>
                )}

                {!effectiveCollapsed && isAuthenticated && !isAdminView && (
                    /* px-2 wrapper mirrors the VehicleSelector row above. Without it this
                       button's w-full spanned the full sidebar while "Add Vehicle" sat inset
                       by 8px a side, which is what made this one look wider. */
                    <div className="w-full shrink-0 mb-2.5 px-2">
                        <button
                            onClick={handleNewThread}
                            className="dash-pixel-fill w-full flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/70 border border-black/5 hover:border-[#D81F26] shadow-sm hover:shadow-[0_8px_20px_rgba(216,31,38,0.25)] text-black/70 hover:text-white focus-visible:text-white hover:scale-[1.02] transition-[color,box-shadow,border-color,transform] duration-300 ease-out active:scale-[0.98] group"
                        >
                            <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                                {/* The plus turns a quarter and goes white as the red fill sweeps in. */}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#D81F26] group-hover:text-white group-focus-visible:text-white group-hover:rotate-90 group-hover:scale-125 transition-[transform,color] duration-300 ease-out">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </span>
                            <span className="font-semibold text-[12px]">New Chat</span>
                        </button>
                    </div>
                )}

                {effectiveCollapsed && isAuthenticated && !isAdminView && (
                    <button
                        onClick={handleNewThread}
                        title="New Chat"
                        className="dash-pixel-fill p-2.5 rounded-xl bg-white/70 border border-black/5 hover:border-[#D81F26] shadow-sm text-[#D81F26] hover:text-white hover:scale-105 transition-[color,box-shadow,border-color,transform] duration-300 ease-out active:scale-95 mb-2"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    </button>
                )}

                <nav className={`space-y-0.5 w-full shrink-0 mb-3 ${effectiveCollapsed ? 'px-1' : 'px-1.5'}`}>
                    {navItems.map((item) => {
                        const isActive = (item.activePaths || [item.path]).includes(location.pathname);
                        const badgeCount = item.badge || 0;
                        return (
                            <button
                                key={item.name}
                                onClick={() => handleNavClick(item.path)}
                                className={`relative w-full flex items-center transition-[background-color,color,box-shadow] duration-150 ease-out group ${effectiveCollapsed ? 'justify-center py-2.5 rounded-xl' : 'gap-3 px-3.5 py-2.5 rounded-full'} ${isActive ? 'bg-white text-[#1F1F1F] shadow-[0_2px_10px_rgba(0,0,0,0.06)] ring-1 ring-[#D81F26]/15' : 'text-black/60 hover:bg-white/55'}`}
                                title={effectiveCollapsed ? item.name : ''}
                            >
                                {isActive && !effectiveCollapsed && (
                                    <span className="absolute inset-y-0 left-1 my-auto h-4 w-[3px] rounded-full bg-[#D81F26] animate-dash-selection-pop" aria-hidden="true" />
                                )}
                                <div className={`relative flex items-center justify-center transition-colors ${isActive ? 'text-[#D81F26]' : 'text-black/30 group-hover:text-black'} w-5 h-5 shrink-0`}>
                                    {React.cloneElement(item.icon, { width: '20', height: '20' })}
                                    {effectiveCollapsed && badgeCount > 0 && (
                                        <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#e02424] px-1 text-[9px] font-black leading-none text-white ring-2 ring-[#F9F9F9]">
                                            {badgeCount > 9 ? '9+' : badgeCount}
                                        </span>
                                    )}
                                </div>
                                {!effectiveCollapsed && (
                                    <span className="font-bold text-[15px] transition-colors group-hover:text-black whitespace-nowrap">
                                        {item.name}
                                    </span>
                                )}
                                {!effectiveCollapsed && badgeCount > 0 && (
                                    <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e02424] px-1.5 text-[11px] font-black leading-none text-white">
                                        {badgeCount > 99 ? '99+' : badgeCount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {!effectiveCollapsed && isAuthenticated && !isAdminView && (
                    <div className="flex flex-1 min-h-0 flex-col mb-2 -mx-1 px-1 pr-1">
                        <div className="shrink-0 px-1.5 pb-2">
                            <div className="mb-1.5 flex items-center justify-between gap-2">
                                <p className="text-[10px] font-black uppercase tracking-widest text-black/40">{showArchived ? 'Archived' : 'Conversations'}</p>
                                <button
                                    type="button"
                                    onClick={() => setShowArchived((prev) => !prev)}
                                    className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold transition-colors ${
                                        showArchived
                                            ? 'bg-[#414141] text-white shadow-sm'
                                            : 'text-[#414141] hover:bg-white/70 hover:text-[#1F1F1F]'
                                    }`}
                                    title={showArchived ? 'Back to active conversations' : 'View archived conversations'}
                                >
                                    {showArchived ? (
                                        <>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                                            Active
                                        </>
                                    ) : (
                                        <>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><path d="M10 12h4" /></svg>
                                            Archived
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className={`mb-1.5 flex h-8 items-center gap-1.5 rounded-xl bg-white/65 px-2.5 text-black/35 shadow-sm ring-1 ring-black/5 focus-within:bg-white focus-within:ring-[#D81F26]/60 ${showArchived ? 'hidden' : ''}`}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                    <circle cx="11" cy="11" r="8" />
                                    <path d="m21 21-4.35-4.35" />
                                </svg>
                                <input
                                    type="search"
                                    value={conversationSearch}
                                    onChange={(event) => setConversationSearch(event.target.value)}
                                    placeholder="Search conversations..."
                                    className="min-w-0 flex-1 bg-transparent text-[12px] font-medium text-[#414141] placeholder:text-black/30 outline-none"
                                />
                            </div>

                            <div className={`mb-2 flex flex-wrap items-center gap-1 ${showArchived ? 'hidden' : ''}`}>
                                {conversationFilters.map((filter) => {
                                    const selected = conversationFilter === filter.id;
                                    return (
                                        <button
                                            key={filter.id}
                                            type="button"
                                            onClick={() => setConversationFilter(filter.id)}
                                            className={`h-6 rounded-full px-2.5 text-[11px] font-bold transition-[background-color,color,box-shadow] ${
                                                selected
                                                    ? 'bg-[#414141] text-white shadow-sm'
                                                    : 'bg-white/55 text-[#414141] ring-1 ring-black/10 hover:bg-white'
                                            }`}
                                        >
                                            {filter.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="dash-sidebar-scrollbar flex-1 min-h-0 overflow-y-auto px-1.5">
                            <div className="space-y-0.5 pb-1">
                                {showArchived ? (
                                    archivedLoading && archivedItems.length === 0 ? (
                                        [1, 2, 3].map(i => (
                                            <div key={i} className="h-[56px] rounded-xl bg-black/5 animate-pulse" />
                                        ))
                                    ) : archivedItems.length > 0 ? (
                                        archivedItems.map((t) => {
                                            const archTitle = getThreadTitle(t);
                                            const archVehicle = getThreadVehicleLabel(t);
                                            return (
                                                <div
                                                    key={`archived:${t.thread_id}`}
                                                    className="flex w-full items-stretch rounded-xl transition-[background-color] duration-150 hover:bg-white/50"
                                                >
                                                    <div className="min-w-0 flex-1 px-2 py-1.5">
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="flex h-5 w-5 shrink-0 items-center justify-center text-black/30">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><path d="M10 12h4" /></svg>
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="min-w-0 truncate text-[13px] font-semibold leading-5 text-[#414141]">{archTitle}</p>
                                                                {archVehicle && (
                                                                    <p className="min-w-0 truncate rounded-md bg-white/38 px-1.5 py-0.5 text-[11px] font-medium leading-4 text-[#414141] ring-1 ring-black/[0.035] w-fit mt-0.5">{archVehicle}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRestore(t.thread_id)}
                                                        className="mr-1 my-1 flex shrink-0 items-center gap-1 rounded-lg px-2 text-[11px] font-bold text-[#414141] transition-colors hover:bg-white/70 hover:text-[#D81F26]"
                                                        title="Unarchive conversation"
                                                        aria-label="Unarchive conversation"
                                                    >
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6" /><path d="M3 13a9 9 0 1 0 3-7.7L3 8" /></svg>
                                                        Unarchive
                                                    </button>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className="px-3 py-3 text-center text-[11px] font-medium text-black/30">No archived conversations.</p>
                                    )
                                ) : conversationsLoading && conversationItems.length === 0 ? (
                                    [1, 2, 3].map(i => (
                                        <div key={i} className="h-[56px] rounded-xl bg-black/5 animate-pulse" />
                                    ))
                                ) : visibleConversationItems.length > 0 ? (
                                    visibleConversationItems.map((item) => {
                                        // Any conversation backed by a thread can be archived (incl. diagnosis cards).
                                        // Rename is available for every conversation — a chat thread renames its
                                        // title, a diagnosis conversation renames its ticket (and thread if present).
                                        const canManage = Boolean(item.threadId);
                                        const canRename = Boolean(item.threadId) || Boolean(item.ticketId);
                                        const isEditing = editingKey != null && editingKey === item.key;
                                        if (isEditing) {
                                            return (
                                                <div key={item.key} className="flex w-full items-center gap-1 rounded-xl bg-[#F9F9F9] px-2 py-1.5 shadow-sm">
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={editingTitle}
                                                        onChange={(e) => setEditingTitle(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                                                            else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                                                        }}
                                                        maxLength={120}
                                                        className="min-w-0 flex-1 rounded-lg bg-white px-2 py-1 text-[13px] font-semibold text-[#414141] outline-none ring-1 ring-[#D81F26]/60"
                                                    />
                                                    <button type="button" onClick={commitRename} title="Save" aria-label="Save title" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[#D81F26] hover:bg-white/70">
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                    </button>
                                                    <button type="button" onClick={cancelRename} title="Cancel" aria-label="Cancel rename" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-black/40 hover:bg-white/70">
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                                    </button>
                                                </div>
                                            );
                                        }
                                        return (
                                        <div
                                            key={item.key}
                                            className={`group/row relative flex w-full items-stretch rounded-xl transition-[background-color,box-shadow,transform] duration-150 ease-out active:scale-[0.99] ${
                                                item.isActive
                                                    ? 'bg-white shadow-[0_2px_10px_rgba(0,0,0,0.06)] ring-1 ring-black/5 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-[#D81F26]'
                                                    : 'hover:bg-white/50'
                                            } ${item.disabled ? 'cursor-default' : ''}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (item.garageRequestId) handleOpenGarageRequest(item.garageRequestId, item.garageRequest);
                                                    else if (item.threadId) handleSelectThread(item.threadId);
                                                }}
                                                disabled={item.disabled}
                                                className="min-w-0 flex-1 px-2 py-1.5 text-left"
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <div className={`flex h-5 w-5 shrink-0 items-center justify-center ${getConversationIconClass(item.kind)}`}>
                                                        {getConversationIcon(item.kind)}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex min-w-0 items-center justify-between gap-1">
                                                            <p className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-5 text-[#414141]">
                                                                {item.title}
                                                            </p>
                                                            {item.statusLabel ? (
                                                                <span className={`shrink-0 truncate rounded-full px-1.5 py-0.5 text-[9px] font-black leading-3 ${item.statusClasses}`}>
                                                                    {item.statusLabel}
                                                                </span>
                                                            ) : item.timeLabel ? (
                                                                <span
                                                                    className="shrink-0 truncate whitespace-nowrap text-right text-[10px] font-medium leading-4 text-[#414141]"
                                                                    title={item.timeTooltip}
                                                                >
                                                                    {item.timeLabel}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <div className="flex min-w-0 items-center justify-between gap-1.5">
                                                            {item.vehicleLabel && (
                                                                <p className="min-w-0 truncate rounded-md bg-white/38 px-1.5 py-0.5 text-[11px] font-medium leading-4 text-[#414141] ring-1 ring-black/[0.035]">
                                                                    {item.vehicleLabel}
                                                                </p>
                                                            )}
                                                            <div className="ml-auto flex shrink-0 items-center gap-1">
                                                                {item.kind !== 'general' && !item.statusLabel && (
                                                                    <span className="shrink-0 rounded-full bg-white/60 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-[#414141]">
                                                                        {item.kindLabel}
                                                                    </span>
                                                                )}
                                                                {item.statusLabel && item.timeLabel && (
                                                                    <span
                                                                        className="shrink-0 truncate whitespace-nowrap text-right text-[10px] font-medium leading-4 text-[#414141]"
                                                                        title={item.timeTooltip}
                                                                    >
                                                                        {item.timeLabel}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => togglePinnedConversation(item.key)}
                                                className={`mr-1 my-1 flex w-7 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                                    item.isPinned
                                                        ? 'text-[#414141] hover:bg-white/70'
                                                        : 'text-black/15 hover:bg-white/70 hover:text-[#414141]'
                                                }`}
                                                title={item.isPinned ? 'Unpin conversation' : 'Pin conversation'}
                                                aria-label={item.isPinned ? 'Unpin conversation' : 'Pin conversation'}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill={item.isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M12 2l2.9 6.1 6.7.9-4.8 4.7 1.2 6.6L12 17.1 6 20.3l1.2-6.6L2.4 9l6.7-.9z" />
                                                </svg>
                                            </button>
                                            {(canManage || canRename) && (
                                                <div className="relative flex" ref={openActionMenuKey === item.key ? actionMenuRef : null}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setOpenActionMenuKey((prev) => (prev === item.key ? null : item.key))}
                                                        className="mr-1 my-1 flex w-7 shrink-0 items-center justify-center rounded-lg text-black/25 transition-colors hover:bg-white/70 hover:text-[#414141]"
                                                        title="Conversation options"
                                                        aria-label="Conversation options"
                                                        aria-haspopup="menu"
                                                        aria-expanded={openActionMenuKey === item.key}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
                                                    </button>
                                                    {openActionMenuKey === item.key && (
                                                        <div role="menu" onMouseDown={(e) => e.stopPropagation()} className="absolute right-1 top-9 z-50 w-36 overflow-hidden rounded-xl border border-black/5 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.12)]">
                                                            {canRename && (
                                                                <button type="button" role="menuitem" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); startRename(item); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-semibold text-[#414141] hover:bg-black/5">
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                                                                    Rename
                                                                </button>
                                                            )}
                                                            {canManage && (
                                                                <button type="button" role="menuitem" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleArchive(item.threadId); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-semibold text-[#414141] hover:bg-black/5">
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><path d="M10 12h4" /></svg>
                                                                    Archive
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })
                                ) : (
                                    <p className="px-3 py-3 text-center text-[11px] font-medium text-black/30">
                                        {conversationItems.length === 0
                                            ? 'No conversations yet.'
                                            : 'No conversations match this filter.'}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className={`mt-auto border-t border-black/5 p-2 pb-4 relative flex flex-col shrink-0 ${effectiveCollapsed ? 'items-center' : ''}`} ref={menuRef}>
                {isMenuOpen && (
                    <div className={`absolute bottom-full mb-2 bg-white rounded-2xl border border-black/5 shadow-[0_10px_40px_rgba(0,0,0,0.1)] overflow-hidden z-50 ${effectiveCollapsed ? 'left-1 w-52' : 'left-2 right-2'}`}>
                        <div className="p-1.5">
                            {isLoggedIn && (
                                <div className="px-3 py-2 mb-1 border-b border-black/5">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-black/30">Account</p>
                                    {isAdminView ? (
                                        <p className="mt-1 text-[11px] font-bold text-black">{displayName || 'Shop Admin'}</p>
                                    ) : (
                                        <div
                                            className="mt-1 flex min-w-0 items-center gap-1.5"
                                            title={accountPhoneParts.label}
                                        >
                                            {accountPhoneParts.countryCode && (
                                                <span className="shrink-0 rounded-full bg-[#F9F9F9] px-1.5 py-0.5 text-[10px] font-black leading-none text-[#414141]">
                                                    {accountPhoneParts.countryCode}
                                                </span>
                                            )}
                                            <span className="min-w-0 truncate text-[11px] font-bold text-black">
                                                {accountPhoneParts.nationalNumber}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!isAdminView && (
                                <>
                                    <button
                                        onClick={() => { handleNavClick('/settings'); setIsMenuOpen(false); }}
                                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs font-bold text-black hover:bg-black/5 transition-colors rounded-xl"
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                                        Settings
                                    </button>

                                    <button
                                        onClick={() => { onOpenHelp(); setIsMenuOpen(false); }}
                                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs font-bold text-black hover:bg-black/5 transition-colors rounded-xl"
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>
                                        Help & Support
                                    </button>
                                </>
                            )}

                            {isLoggedIn && (
                                <div className="border-t border-black/5 mt-1 pt-1">
                                    <button
                                        onClick={() => { onLogout(); setIsMenuOpen(false); }}
                                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs font-bold text-rose-500 hover:bg-rose-50 transition-colors rounded-xl"
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
                                        Log out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {effectiveCollapsed ? (
                    <div className="flex flex-col items-center gap-2">
                        {!isLoggedIn && (
                            <button
                                type="button"
                                onClick={onOpenAuthModal}
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-transparent text-black/45 transition-[background-color,color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:scale-110 hover:bg-white/70 hover:text-[#414141] hover:shadow-[0_8px_18px_rgba(0,0,0,0.12)] active:scale-95"
                                title="Login"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                    <path d="m10 17 5-5-5-5" />
                                    <path d="M15 12H3" />
                                </svg>
                            </button>
                        )}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="flex items-center justify-center rounded-xl bg-white/50 border border-black/5 hover:bg-white transition-[background-color,box-shadow,transform] duration-150 ease-out shadow-sm w-10 h-10 shrink-0 active:scale-95"
                            title={isAdminView ? 'Shop Admin' : 'Account & Settings'}
                        >
                            <DriverAvatar src={sidebarAvatarUrl} glyph={sidebarAvatarGlyph} className="w-8 h-8" />
                        </button>
                    </div>
                ) : (
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setIsMenuOpen(!isMenuOpen);
                            }
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/70 border border-black/6 hover:bg-white shadow-sm transition-[background-color,box-shadow,transform] duration-150 ease-out group min-w-0 active:scale-[0.99]"
                    >
                        <DriverAvatar src={sidebarAvatarUrl} glyph={sidebarAvatarGlyph} className="w-8 h-8" />

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                                <p className="text-base font-bold text-black truncate leading-tight">
                                    {isLoading ? '…' : isAdminView ? (displayName || 'Shop Admin') : isAuthenticated ? (displayName || formatAccountLabel(authState.phone, authState.email)) : 'Guest'}
                                </p>
                                {/* #711: "About CarFido" button opened the infographic intro — disabled
                                {!isAdminView && (
                                    <button
                                        onClick={e => { e.stopPropagation(); onOpenOnboarding(); }}
                                        title="About CarFido"
                                        className="shrink-0 w-5 h-5 rounded-full bg-black/6 hover:bg-black/10 flex items-center justify-center text-black/30 hover:text-[#414141] transition-[background-color,color,transform] duration-150 ease-out active:scale-95"
                                    >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                                    </button>
                                )}
                                */}
                            </div>
                            <p className={`text-[11px] font-bold tracking-wide ${testerMode ? 'text-amber-500' : 'text-black/35'}`}>
                                {isAdminView ? (isPartnerSite() ? 'Partner Portal' : 'Admin Mode') : testerMode ? 'Tester Mode' : isAuthenticated ? 'Standard Plan' : 'Not logged in'}
                            </p>
                        </div>

                        {!isLoggedIn && !isLoading && (
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onOpenAuthModal();
                                    setIsMenuOpen(false);
                                }}
                                title="Login"
                                className="shrink-0 inline-flex h-8 items-center justify-center rounded-full bg-[#D81F26] px-3 text-[11px] font-black uppercase tracking-[0.08em] text-white shadow-[0_8px_18px_rgba(216,31,38,0.18)] transition-[background-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-[#B8181E] hover:shadow-[0_10px_22px_rgba(216,31,38,0.24)] active:scale-95"
                            >
                                Login
                            </button>
                        )}

                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-black/15 group-hover:text-black/40 transition-colors"><path d="m18 15-6-6-6 6"/></svg>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatSidebar;
